import type { Editor } from '@tiptap/core';
import { FC, MutableRefObject, useCallback, useEffect, useRef, useState, type KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useTranslation } from 'react-i18next';

import {
	ActionDecorations,
	ActionFeedback,
	ActionInput,
	ActionMedia,
	ActionSubmit,
	ActionText,
	AmountButton,
	GiverToggle,
	useActionInput,
} from '@bonusly/common-action-input';
import { CommonCard, useGivePointsCapabilities } from '@bonusly/common-give-box';
import { Box, Flex, Hide, rem, Spacer, StyleProps, useMediaQuery } from '@bonusly/design-system';
import { useDispatch } from '@bonusly/events';
import { useCurrentUser, useIsBizyEnabledForUser } from '@bonusly/who-am-i';

import { recordCommentAssistAcceptance } from '../../../../../queries/bizy-comment-suggestions';
import { Bonus } from '../../../../../types/bonus';
import { useBizyFlightStore } from '../../../../bizy';
import { useBizyContextualSlash } from '../../../../bizy-side-panel/hooks/use-bizy-contextual-slash';
import { useBizyActiveComposersStore } from '../../../../bizy-side-panel/store/use-bizy-active-composers-store';
import { AmountChip } from '../../../give-box/components/amount-chip';
import { FlashPointsChip } from '../../../give-box/components/flash-points-chip';
import { UserAvatar } from '../../components/user-avatar';
import { CommentEvent } from '../../events';
import { BizyCommentAssistBee, type BizyCommentAssistBeeHandle, type CommentAssistCoachingPhase } from './bizy-comment-assist-bee';
import { BizyCommentSuggestions } from './bizy-comment-suggestions';
import { commentAssistCoachingPhase, suggestionPickerBeePose } from './comment-suggestion-helpers';
import { COMMENT_ZONE_ATTR, useBizyCommentBoxFlight } from './hooks/use-bizy-comment-box-flight';
import { useBizyCommentSuggestions } from './hooks/use-bizy-comment-suggestions';
import { useCommentInput } from './hooks/use-comment-input';
import { SuggestedComments } from './suggested-comments';

const BIZY_COMMENT_FLIGHT_DESKTOP_MIN_PX = 1024;
const COACHING_DEBOUNCE_MS = 400;

/** Reads editor plain text from inside ActionInput and reports it upstream. */
const CommentAssistDraftWatcher: FC<{ onDraftChange: (text: string) => void }> = ({ onDraftChange }) => {
	const { state } = useActionInput();
	useEffect(() => {
		onDraftChange(state.plainText ?? '');
	}, [state.plainText, onDraftChange]);
	return null;
};

export interface CommentComposerProps extends StyleProps {
	id?: string;
	bonus: Bonus;
	initalText?: string;
	onSuccess?: VoidFunction;
	commentBoxRef?: MutableRefObject<HTMLDivElement | null>;
	/** True when rendered inside the post modal — flips the Bizy bubble left so it doesn't overflow the modal. */
	isInModal?: boolean;
}

const DynamicStyledActionText = ({ compact = false }: { compact?: boolean }) => {
	const { attachment, isLoadingAttachment } = useActionInput();
	// The default 150px min-height parks thank-you chips at the bottom of a tall
	// empty editor. While Bizy suggestions are open we only collapse minHeight —
	// never pass padding via inputStyles. RichMediaInput applies those styles to
	// EditorContent and `.tiptap`, so a padding override doubles top padding and
	// shifts the "Write a comment…" placeholder when Give me ideas opens.
	const minHeight = compact || attachment || isLoadingAttachment ? undefined : rem(150);
	return <ActionText inputStyles={{ minHeight }} />;
};

export const CommentComposer: FC<CommentComposerProps> = ({ id, bonus, initalText, onSuccess, commentBoxRef, isInModal = false }) => {
	const { t } = useTranslation('recognition');
	const user = useCurrentUser();
	const isBizyEnabledForUser = useIsBizyEnabledForUser();
	const dispatch = useDispatch();
	const { isOwnRecognition, onSuggestionSelected, onSuggestionCleared, ...config } = useCommentInput(bonus, {
		onSuccess,
	});

	// Mirror the give-box header chips in the comment box so people can see their
	// balance before drafting an add-on, using the same rules as giving (INS-1447).
	const { canGivePoints: showPointsChips, canGiveAmount: showAmountChip } = useGivePointsCapabilities();
	const amountChipBalance = config.budget.monthlyAllowanceBalance ?? 0;

	// The comment-box Bizy flight runs on every viewport; the bee's perch
	// placement is chosen from the viewport below. (`useMediaQuery` returns
	// false on the first render then settles, so the gate stays live across
	// SSR/hydration rather than flickering off.)
	const [isDesktop] = useMediaQuery(`(min-width: ${BIZY_COMMENT_FLIGHT_DESKTOP_MIN_PX}px)`);
	const isMobileBee = !isDesktop;
	const commentCardRef = useRef<HTMLDivElement>(null);
	const beeAnchorRef = useRef<HTMLButtonElement>(null);
	const beeRef = useRef<BizyCommentAssistBeeHandle>(null);
	const playGesture = useBizyFlightStore((state) => state.playGesture);
	const setParkedState = useBizyFlightStore((state) => state.setParkedState);
	const {
		isOwner: isBeeOwner,
		hasLanded: hasBeeLanded,
		claim: claimBee,
	} = useBizyCommentBoxFlight({
		bonusId: bonus.id,
		enabled: isBizyEnabledForUser,
		cardRef: commentCardRef,
		anchorRef: beeAnchorRef,
	});
	const {
		isOpen: isSuggestionsOpen,
		mode: suggestionsMode,
		openIdeas: openSuggestions,
		openRefine,
		clear: clearSuggestions,
		regenerate: regenerateSuggestions,
		suggestions,
		isLoading: isSuggestionsLoading,
		isError: isSuggestionsError,
		conversationIdRef: suggestionsConversationIdRef,
	} = useBizyCommentSuggestions({
		bonusId: bonus.id,
		// Keep the feature gate independent of bee ownership so an open Give me
		// ideas picker survives the bee flying to another box and is still there
		// when it flies back. Ownership is passed separately so refine (draft-
		// coupled) can still dismiss on fly-off.
		enabled: isBizyEnabledForUser,
		isOwner: isBeeOwner,
	});

	const [draftText, setDraftText] = useState('');
	const [coachingPhase, setCoachingPhase] = useState<CommentAssistCoachingPhase>('none');
	// After accepting a Make it shine pick, don't offer refine again until the
	// draft is cleared (start over).
	const [refineAccepted, setRefineAccepted] = useState(false);
	// Once the user picks a Give me ideas chip, stop offering Make it shine too
	// (they picked a lane). Set on pick — not on opening — so dismissing ideas
	// without a selection still re-offers Make it shine (matches refineAccepted
	// semantics). Resets when the draft is cleared (start over). While the ideas
	// picker is open Make it shine is already hidden via suppressBubble.
	const [ideasStarted, setIdeasStarted] = useState(false);
	// After a pick, suppress the resting/coaching bubble so Bizy sits quietly
	// while the user edits; tapping the bee still opens the panel.
	const [hideRestingHint, setHideRestingHint] = useState(false);
	// Guards the "start over" reset below from the transient empty-draft window
	// during a pick: onSuggestionSelected resets the editor before inserting
	// the chosen text, so draftText briefly reads ''. Without this guard the
	// reset effect would fire on that blip and undo the pick we just made
	// (re-showing the resting bubble and Make it shine). Set at pick time and
	// cleared by the reset effect once it observes draftText go non-empty.
	const pickInFlightRef = useRef(false);

	const handleDraftChange = useCallback((text: string) => {
		setDraftText(text);
	}, []);

	// "Start over" reset: when the draft becomes empty, re-offer Make it shine
	// and clear the post-pick hint. This is intentionally NOT gated on bee
	// ownership: Escape / scroll-away can send the bee home while focus stays
	// in the editor, and the draft watcher below stays mounted while the
	// feature is on, so draftText stays fresh and this effect still observes
	// the empty transition while the bee is away. Without this, an ideas pick
	// followed by a bee fly-off would leave suppressMakeItShine true even
	// after the user cleared the draft and started fresh.
	useEffect(() => {
		if (!isBizyEnabledForUser) return;
		if (draftText.trim() !== '') {
			// Real content is present (a pick landed, or the user typed): the
			// pick has fully settled. Clear the guard HERE — keyed on the
			// parent's draftText — rather than in handleDraftChange. The draft
			// watcher is a child, so its effect runs before this one in the same
			// commit; clearing there would drop the guard while this effect still
			// sees the stale empty draftText and wrongly run the reset below.
			pickInFlightRef.current = false;
			return;
		}
		// A pick is in flight: onSuggestionSelected resets the editor before
		// inserting the chosen text, so draftText briefly reads ''. Skip the
		// reset here so we don't undo the pick (re-showing the resting bubble
		// and Make it shine) before the inserted text lands.
		if (pickInFlightRef.current) return;
		setRefineAccepted(false);
		setIdeasStarted(false);
		setHideRestingHint(false);
		// The user wiped the draft to start fresh: drop any picked-suggestion
		// attribution so a from-scratch comment isn't mislabeled "Sent with Bizy's help".
		onSuggestionCleared();
		// Refine continuations are grounded in the draft; if the draft is
		// cleared while Make it shine chips are still open, dismiss the picker
		// so a stale continuation can't be picked and appended as a stub
		// fragment. Ideas are cold full comments and stay open (they don't
		// depend on the draft). Reset the parked bee to `curious` so it doesn't
		// keep pointing/thinking at an empty box (matches the pick, clear-
		// button, and empty-draft pick-guard dismiss paths).
		if (isSuggestionsOpen && suggestionsMode === 'refine') {
			clearSuggestions();
			setParkedState('curious');
		}
	}, [draftText, isBizyEnabledForUser, isSuggestionsOpen, suggestionsMode, clearSuggestions, setParkedState, onSuggestionCleared]);

	// Coaching phase promotion is gated on bee ownership: when the bee is away
	// there's nothing to coach against, so drop to `none`. Only debounce
	// promotion to `ready` to avoid flicker while typing past the threshold;
	// demote (ready → short/none) immediately so Make it shine can't linger
	// after the user clears or shortens the draft.
	useEffect(() => {
		if (!isBizyEnabledForUser || !isBeeOwner) {
			setCoachingPhase('none');
			return;
		}

		const next = commentAssistCoachingPhase(draftText);
		if (next !== 'ready') {
			setCoachingPhase(next);
			return;
		}

		const timer = window.setTimeout(() => {
			setCoachingPhase('ready');
		}, COACHING_DEBOUNCE_MS);
		return () => window.clearTimeout(timer);
	}, [draftText, isBizyEnabledForUser, isBeeOwner]);

	const handleGiveMeIdeas = useCallback(() => {
		playGesture({ kind: 'tap', dx: -8, dy: 0 });
		openSuggestions();
	}, [playGesture, openSuggestions]);

	const handleMakeItShine = useCallback(() => {
		playGesture({ kind: 'tap', dx: -8, dy: 0 });
		openRefine(draftText);
	}, [playGesture, openRefine, draftText]);

	// While suggestions are loading the parked bee searches toward the box; once chips arrive it
	// points at them (bee sits on the right of the box → pointingLeft). On error
	// or empty results it returns to curious so it isn't pointing at nothing.
	useEffect(() => {
		if (!isBizyEnabledForUser || !isBeeOwner || !isSuggestionsOpen) return;
		setParkedState(
			suggestionPickerBeePose({
				isLoading: isSuggestionsLoading,
				isError: isSuggestionsError,
				suggestionsCount: suggestions.length,
			})
		);
	}, [isBizyEnabledForUser, isBeeOwner, isSuggestionsOpen, isSuggestionsLoading, isSuggestionsError, suggestions.length, setParkedState]);

	const handleBizySuggestionSelect = useCallback(
		(text: string) => {
			// Refine continuations are grounded in the draft. If the draft was
			// cleared while Make it shine chips stayed open, appending the
			// continuation would insert a non-sentence stub, so dismiss instead
			// (mirrors regenerate's empty-draft dismiss). The draft watcher also
			// proactively dismisses refine on an empty draft, so this is a
			// defense-in-depth guard at pick time.
			if (suggestionsMode === 'refine' && !draftText.trim()) {
				clearSuggestions();
				setParkedState('curious');
				return;
			}
			// Capture the conversation id before clearSuggestions() nulls the ref.
			const conversationId = suggestionsConversationIdRef.current;
			// Mark a pick in flight so the "start over" reset effect ignores the
			// transient empty-draft window created by the editor reset below.
			pickInFlightRef.current = true;
			onSuggestionSelected(text, {
				source: 'bizy_comment_assist',
				// Make it shine returns continuations that extend the draft; ideas
				// are cold full-comment replacements.
				append: suggestionsMode === 'refine',
			});
			// Record adoption so Bizy conversation scoring credits this session
			// (action_taken / job_completion). Fire-and-forget; failures swallowed.
			if (conversationId) {
				void recordCommentAssistAcceptance({ bonusId: bonus.id, conversationId, mode: suggestionsMode });
			}
			if (suggestionsMode === 'refine') {
				setRefineAccepted(true);
			} else {
				setIdeasStarted(true);
			}
			clearSuggestions();
			setParkedState('curious');
			setHideRestingHint(true);
		},
		[onSuggestionSelected, suggestionsMode, draftText, clearSuggestions, setParkedState, suggestionsConversationIdRef, bonus.id]
	);

	const handleClearSuggestions = useCallback(() => {
		clearSuggestions();
		setParkedState('curious');
	}, [clearSuggestions, setParkedState]);

	const handleRegenerateSuggestions = useCallback(() => {
		regenerateSuggestions(suggestionsMode === 'refine' ? draftText : undefined);
	}, [regenerateSuggestions, suggestionsMode, draftText]);

	// The composer only mounts when the user opens a thread (e.g. clicking the
	// "Comment" button), which doesn't touch the card's focus/pointerdown
	// handlers — so claim on mount too. Otherwise opening a box via its button
	// (rather than clicking into the input) wouldn't move the bee.
	useEffect(() => {
		if (isBizyEnabledForUser) claimBee();
	}, [isBizyEnabledForUser, claimBee]);

	// HABITS-1294: intercept `/` at the start of a comment to open Bizy
	// contextually with the parent post's context so users can draft a reply
	// without leaving the thread. When the current user authored the post,
	// we flip the framing so Bizy doesn't suggest "thanking yourself" — the
	// user is most likely following up with the original receivers.
	const author = bonus.giver;
	const currentUserIsAuthor = !!author?.id && author.id === user.id;
	const receivers = bonus.receivers ?? [];
	// Names sent to Bizy for ADDRESSING (i.e. "Thanks Sid," or "Sid,
	// you handled that so well"). Always prefer `preferred_first_name`
	// over `first_name` over `display_name` — the legal full name is
	// usually wrong here. Bonusly users set `preferred_first_name` to
	// "Sid" / "Maddy" / etc.; using "Sidney Bynum" makes Bizy comments
	// stand out as bot-written.
	const preferredFirstNameOf = (u: { preferred_first_name?: string; first_name?: string; display_name?: string } | undefined): string | undefined =>
		u?.preferred_first_name?.trim() || u?.first_name?.trim() || u?.display_name?.trim() || undefined;
	const receiverFirstNames = receivers.map((r) => preferredFirstNameOf(r)).filter((n): n is string => !!n);
	const formatPeopleList = (names: string[]): string | undefined => {
		if (names.length === 0) return undefined;
		if (names.length === 1) return names[0];
		if (names.length === 2) return `${names[0]} and ${names[1]}`;
		return `${names[0]} and ${names.length - 1} others`;
	};
	const authorFirstName = preferredFirstNameOf(author);
	// `recipientLabel` drives the Bizy starter sentence ("I want to comment
	// on Sam's post — help me figure out what to add."), so use the
	// first-name version too for consistency with the addressing names.
	const recipientLabel = currentUserIsAuthor ? formatPeopleList(receiverFirstNames) : authorFirstName;
	const defaultPlaceholder = isOwnRecognition ? t('comment.input_thanks_placeholder') : t('comment.input_placeholder');
	const focusedPlaceholder = config.placeholder ?? defaultPlaceholder;
	const placeholder = useCallback(
		({ editor }: { editor: Editor }) => (editor.isFocused ? focusedPlaceholder : defaultPlaceholder),
		[defaultPlaceholder, focusedPlaceholder]
	);

	// Bring the composer back into view when Bizy hands a draft back. The
	// composer might be off-screen (user scrolled while chatting with Bizy
	// in the side panel), so scroll the container into view before / after
	// inserting and focus the editor.
	const bringComposerIntoFocus = () => {
		const handle = config.ref.current;
		if (!handle) return;
		commentBoxRef?.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
		handle.focus();
	};

	// HABITS-1294: register this composer in the global active-composers
	// store so the Bizy UI block dispatcher can route Insert / Send now
	// actions to the right composer (keyed by bonusId — multiple
	// composers can be open across the feed). The handle is stable
	// across re-renders because it reads from refs at call time, so we
	// only need to re-register if the bonus id itself changes.
	const registerCommentComposer = useBizyActiveComposersStore((state) => state.registerCommentComposer);
	useEffect(() => {
		const insertText = (text: string) => {
			dispatch(CommentEvent.Open, { id: bonus.id });
			const apply = () => {
				const live = config.ref.current;
				if (!live) return;
				live.insertWithHighlight(text);
				bringComposerIntoFocus();
			};
			if (config.ref.current) {
				apply();
			} else {
				requestAnimationFrame(() => requestAnimationFrame(apply));
			}
		};
		const submitNow = (text: string): boolean => {
			dispatch(CommentEvent.Open, { id: bonus.id });
			const submit = () => {
				const live = config.ref.current;
				if (!live) return false;
				live.reset();
				// `insertWithHighlight` lets the user see what's about to
				// be posted (the editor flashes the AI-touched highlight
				// for a beat before the comment is created and the editor
				// resets). This also matches the "Insert draft" visual so
				// the actions look related.
				live.insertWithHighlight(text);
				// Two rAFs: TipTap insertions schedule a transaction;
				// reading `data()` on the same frame can return the
				// pre-insert state. Two frames is enough for the editor
				// to commit the transaction AND for the derived data hook
				// to recompute.
				requestAnimationFrame(() => {
					requestAnimationFrame(() => {
						const data = live.data();
						if (data && data.text.trim().length > 0) {
							config.onSubmit(data);
						}
					});
				});
				return true;
			};
			if (config.ref.current) return submit();
			requestAnimationFrame(() => requestAnimationFrame(submit));
			return true;
		};
		return registerCommentComposer(bonus.id, {
			insertDraft: insertText,
			sendNow: submitNow,
			scrollIntoViewAndFocus: bringComposerIntoFocus,
		});
		// eslint-disable-next-line react-hooks/exhaustive-deps -- handle reads from refs; we only re-register when bonus.id changes
	}, [bonus.id, registerCommentComposer]);

	const { handleKeyDownCapture: handleBizySlashKeyDown } = useBizyContextualSlash({
		surface: 'comment',
		enabled: isBizyEnabledForUser,
		getInputHandle: () => config.ref.current,
		getSurfaceData: () => ({
			// Key name matches the backend's
			// `context[:bizy_contextual_parentBonusId]` reader and the
			// frontend action schema's `parentBonusId` field — the
			// dispatcher uses it to route the action to the right
			// composer in the registry.
			parentBonusId: bonus.id,
			parentAuthorId: author?.id ?? null,
			// `parentAuthorName` is the formal display name (kept for any
			// fallback path that needs the full version); `parentAuthorFirstName`
			// is the addressable name Bizy uses for "Thanks Sid," / "Sid,
			// this means a lot...". The addressable version is what any
			// surface-section / addressing-section instruction reads.
			parentAuthorName: author?.display_name ?? null,
			parentAuthorFirstName: authorFirstName ?? null,
			parentReason: bonus.reason ?? null,
			// `currentUserIsAuthor` tells Bizy the user wrote the post they're
			// commenting on (so it shouldn't suggest thanking the author —
			// that's the user themselves). We also forward the receiver
			// names so Bizy can naturally follow up with them.
			currentUserIsAuthor,
			receiverIds: receivers.map((r) => r.id),
			// `receiverNames` here is the addressing version (preferred
			// first name) — that's what every prompt branch reads as
			// `bizy_contextual_receiverNames`. The full display name
			// isn't useful for addressing.
			receiverNames: receiverFirstNames,
			// Pre-existing flag from `useSayThanks(bonus)`: true when the
			// current user is a *receiver* of the recognition (and would
			// typically be saying thanks). Distinct from `currentUserIsAuthor`.
			isOwnRecognition,
		}),
		getRecipientLabel: () => recipientLabel,
		// `insert` / `sendNow` are no longer threaded through the slash
		// hook — actions on a comment_draft_card include the
		// parentBonusId, and the dispatcher looks the composer up in
		// `useBizyActiveComposersStore` (registered by the effect above).
		// That makes clicks idempotent: the registry is alive as long as
		// the composer is mounted, so the user can click Insert / Send
		// now multiple times without the second click falling back to
		// the give-box.
		onDismiss: () => {
			// Bizy panel closed without insert (or after insert finished). Make
			// sure the user can see and act on the composer.
			bringComposerIntoFocus();
		},
	});

	// When the comment-box Bizy flight is on, `/` engages the parked bee (opens
	// its Give me ideas / Make it shine panel) instead of opening the Bizy side
	// panel chat. The bee claims this box on focus, so by the time the user types
	// it's parked here. When the flight is off (or the bee isn't parked here yet),
	// fall back to the contextual-slash behavior that opens the Bizy chat.
	const handleCommentKeyDownCapture = useCallback(
		(event: ReactKeyboardEvent<HTMLElement>) => {
			if (isBizyEnabledForUser && isBeeOwner) {
				if (event.key !== '/' || event.metaKey || event.ctrlKey || event.altKey || event.repeat) return;
				// The bee panel is suppressed while the in-box suggestion picker is
				// open (suppressBubble), so opening it would no-op. Don't swallow
				// `/` in that case — let it insert as a normal character.
				if (isSuggestionsOpen) return;
				const handle = config.ref.current;
				if (!handle) return;
				const currentText = handle.data().text ?? '';
				if (currentText.length > 0) return;
				event.preventDefault();
				event.stopPropagation();
				beeRef.current?.open();
				// Defensive: if a `/` slipped through before preventDefault took
				// effect, reset back to empty so the bee panel doesn't open with
				// a stray `/` left in the draft (mirrors useBizyContextualSlash).
				requestAnimationFrame(() => {
					const after = config.ref.current;
					if (after && after.data().text === '/') {
						after.reset();
					}
				});
				return;
			}
			handleBizySlashKeyDown(event);
		},
		[isBizyEnabledForUser, isBeeOwner, isSuggestionsOpen, handleBizySlashKeyDown]
	);

	return (
		<CommonCard id={id} variant="inlay" bg="bg.subtle" ref={commentBoxRef}>
			<Flex columnGap={2}>
				<Hide below="sm">
					<Box>
						<UserAvatar size="md" user={user} />
					</Box>
				</Hide>

				<Box
					ref={commentCardRef}
					backgroundColor="bg.surface"
					borderRadius="md"
					boxShadow="sm"
					p={3}
					w="full"
					minW={0}
					position="relative"
					// Mark the white box as a Bizy comment zone so the flight hook treats
					// focus/clicks here (and in tagged popovers) as "inside".
					{...(isBizyEnabledForUser ? { [COMMENT_ZONE_ATTR]: '' } : {})}
					onFocusCapture={isBizyEnabledForUser ? claimBee : undefined}
					onPointerDownCapture={isBizyEnabledForUser ? claimBee : undefined}
				>
					{isBizyEnabledForUser && isBeeOwner && (
						// The invisible marker (flight landing spot) lives inside this
						// component, which also renders the resting hint + tap panel anchored
						// to it. The flight overlay draws the actual bee centered on the marker.
						<BizyCommentAssistBee
							ref={beeRef}
							bonusId={bonus.id}
							anchorRef={beeAnchorRef}
							hasLanded={hasBeeLanded}
							onGiveMeIdeas={handleGiveMeIdeas}
							onMakeItShine={handleMakeItShine}
							coachingPhase={coachingPhase}
							suppressBubble={isSuggestionsOpen}
							hideRestingHint={hideRestingHint}
							suppressMakeItShine={refineAccepted || ideasStarted}
							placement="top-right"
							inModal={isInModal}
						/>
					)}
					<ActionInput autofocus="end" content={initalText} {...config} placeholder={placeholder}>
						{/* Keep the draft watcher mounted while the feature is on (not gated on
					bee ownership) so draftText stays fresh even when the bee flies to
					another box — the "start over" reset above relies on observing the
					empty-draft transition while the bee is away. */}
						{isBizyEnabledForUser && <CommentAssistDraftWatcher onDraftChange={handleDraftChange} />}
						<Flex alignItems="center" columnGap={2}>
							<Box>
								<AmountButton />
							</Box>
							<GiverToggle />
							<Spacer />
							{showPointsChips && <FlashPointsChip />}
							{showAmountChip && <AmountChip amount={amountChipBalance} h={8} />}
						</Flex>

						{/* Editor grows with typed text; Bizy suggestions sit in normal flow
						below it so line breaks push the picker down instead of overlapping. */}
						<Box mb={isSuggestionsOpen ? 0 : 2} position="relative" onKeyDownCapture={handleCommentKeyDownCapture}>
							<DynamicStyledActionText compact={isSuggestionsOpen} />
							{isOwnRecognition && !isSuggestionsOpen && !isBizyEnabledForUser && (
								<Box position="absolute" bottom={0} left={0} right={0} zIndex={1}>
									<SuggestedComments bonus={bonus} onSetText={onSuggestionSelected} onSuggestionCleared={onSuggestionCleared} />
								</Box>
							)}
							<ActionFeedback />
						</Box>

						{isSuggestionsOpen && (
							<BizyCommentSuggestions
								bonusId={bonus.id}
								suggestions={suggestions}
								isLoading={isSuggestionsLoading}
								isError={isSuggestionsError}
								mode={suggestionsMode}
								variant={isMobileBee ? 'mobile' : 'web'}
								onSelect={handleBizySuggestionSelect}
								onRegenerate={handleRegenerateSuggestions}
								onClear={handleClearSuggestions}
							/>
						)}

						<ActionMedia w="full" />

						<Flex
							alignItems={{ base: 'stretch', xs: 'flex-start' }}
							direction={{ base: 'column', xs: 'row' }}
							gap={2}
							id="comment-composer-submit-container"
							justifyContent="space-between"
							mt={3}
							w="full"
						>
							<ActionDecorations />
							<Spacer />

							<ActionSubmit size="md" />
						</Flex>
					</ActionInput>
				</Box>
			</Flex>
		</CommonCard>
	);
};
